import axios from "axios";
import _ from "lodash";
// import { toast } from "react-toastify";
import { toast } from "react-toastify";
import { storage } from "./";
const API = "/api";
const HTTP_TOO_MANY_REQUESTS = 429;
const RATE_LIMITED_TOAST_ID = "rate-limited";
const RATE_LIMITED_FALLBACK_MESSAGE = "Too many attempts from your network. Please wait a few minutes and try again.";

// The response interceptor below resolves a failed request with the error itself, so a caller's .then can tell a
// rate-limited reply (already toasted) from a normal failure with this.
const isRateLimited = (result) => result?.response?.status === HTTP_TOO_MANY_REQUESTS;

// The HTTP failures the response interceptor below already tells the user about (401 redirects to sign-in; 403, 404
// and 429 toast), so a caller's own failure toast must skip them or the user gets two.
const INTERCEPTOR_HANDLED_STATUSES = [401, 403, 404, HTTP_TOO_MANY_REQUESTS];
const isHandledByInterceptor = (result) => INTERCEPTOR_HANDLED_STATUSES.includes(result?.response?.status);

// True when a { success, message } call failed: no body (an HTTP error the interceptor resolved), or success === false.
// Toasts the server's own message, else fallbackMessage - unless the interceptor already told the user. Pass a toastId
// when the call can repeat quickly (a typeahead keystroke, two effects on one change) so one failure shows once.
const toastFailure = (response, fallbackMessage, toastId) => {
  const failed = !response?.data || response.data.success === false;
  if (failed && !isHandledByInterceptor(response)) {
    toast.error(response?.data?.message || fallbackMessage, toastId ? { toastId } : undefined);
  }
  return failed;
};

function userToken() {
  return storage.getItem("token");
}

function specToken() {
  return storage.getItem("spec");
}

function commonHeaders() {
  const token = storage.getItem("token");
  return {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
}

const headers = () => Object.assign(commonHeaders(), { "Content-Type": "application/json" });

const multipart_headers = () => Object.assign(commonHeaders(), { "Content-Type": "multipart/form-data" });

const html_content_type_headers = () =>
  Object.assign({}, {
    // "Accept": "application/octet-stream",
    "Access-Control-Allow-Origin": "*",
    "Content-Type": "application/octet-stream"
  });

// A failed blob request carries the server's { success: false, message } JSON as a Blob.
// Returns that friendly message, or null when the body has none or is not JSON.
async function jsonBlobMessage(blob) {
  try {
    const parsed = JSON.parse(await blob.text());
    return parsed?.message || null;
  } catch (err) {
    console.error("Could not read the error message from a blob response:", err);
    return null;
  }
}

function queryString(params) {
  const query = Object.keys(params)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join("&");
  return `${query.length ? "?" : ""}${query}`;
}

axios.interceptors.response.use(
  function (response) {
    // Defer the version check until the user is signed in. For a fresh visitor the first response is
    // Public/Authenticate, and reloading there discards the authenticator-app step after the password
    // was accepted (its ticket is React state only, and a setup code may already be emailed), while a
    // Duo user's reload races the redirect to Duo. Neither store the version nor reload yet, so the first
    // authenticated response performs the check and the reload lands on a signed-in page.
    if (!userToken()) return response;
    const clientVersion = storage.getItem("app-version");
    const serverVersion = response.headers["version"];
    if (clientVersion === null || clientVersion !== serverVersion) {
      if (clientVersion !== undefined && serverVersion !== undefined) {
        storage.setItem("app-version", serverVersion);
        console.warn("This page is about to reload with an updated version...");
        setTimeout(function () {
          // try {
          //   unregister();
          // } catch {
          window.location.reload(true);
          // }
        }, 300);
      }
    }
    return response;
  },
  function (error) {
    if (error.response && error.response.status === 404) {
      toast.error(
        "Not found please check your parameters or contact an admin if this issue persists..."
      );
    }
    if (error.response && error.response.status === 401) {
      console.warn("Unauthorized - redirecting to login");
      // storage.removeItem("loggedOnAt");
      storage.removeItem("currentUser");
      storage.removeItem("currentParticipant");
      storage.removeItem("token");
      setTimeout(function () {
        window.location.href = "/";
      }, 500);
    }
    if (isRateLimited(error)) {
      // The per-IP sign-in limiter's own sentence (it names the wait). toastId keeps a held Enter key to one toast.
      toast.error(error.response.data?.message || RATE_LIMITED_FALLBACK_MESSAGE, { toastId: RATE_LIMITED_TOAST_ID });
    }
    if (error.response && error.response.status === 403) {
      toast.error(
        "Your user account does not have the permissions to take this action"
      );
    }
    return error;
  }
);

let cache = {
  timezones: { list: null, serve_count: 0 },
  countries: { list: null, serve_count: 0 },
  threadSizes: { list: null, serve_count: 0 },
  threadMaterials: { list: null, serve_count: 0 },
  threadColors: { list: null, serve_count: 0 },
  needleSizes: { list: null, serve_count: 0 },
};

const MAX_CACHE_SERVE_COUNT = 20;

function cachedList(urlPath, shapeForSelect = false, key = null) {
  if (key === null) key = urlPath;
  if (
    cache[key].list !== null &&
    cache[key].serve_count < MAX_CACHE_SERVE_COUNT
  ) {
    cache[key].serve_count += 1;
    return new Promise((resolve, reject) => {
      resolve(cache[key].list);
    });
  }
  return axios
    .get(`${API}/${urlPath}`, { headers: headers() }) 
    .then((response)  => {
      if (shapeForSelect) {
        cache[key].list = _.map(response.data, (x) =>({ 
          value: x.id,
          label: x.name,
        }));
      } else {
        cache[key].list = response.data;
      }
      cache[key].serve_count = 0;
      return cache[key].list ; 
    })
    .catch(catchHandler);
}

function catchHandler(e) {
  console.error(e);
}

const getTimezones = () => {
  let list = cachedList("Reference/TimezoneList", false, "timezones");
  return list;
};

const getCountryList = async () => {
  let list = await cachedList("Reference/GetCountryList", false, "countries");
  return list;
};

const getThreadSizes = async () => {
  let list = await cachedList("Reference/GetThreadSizes", false, "threadSizes");
  return list;
};

const getThreadColors = async () => {
  let list = await cachedList("Reference/GetThreadColors", false, "threadColors");
  return list;
};

const getThreadMaterials = async () => {
  let list = await cachedList("Reference/GetThreadMaterials ", false, "threadMaterials");
  return list;
};

const getNeedleSizes = async () => {
  let list = await cachedList("Reference/GetNeedleSizes", false, "needleSizes");
  return list;
};

const post = (url, data) => {
  return axios({
    method: "post",
    url: `${API}/${url}`,
    data: data,
    timeout: 600000,
    headers: headers(),
  });
};

const postWithSignal = (url, data, cancelToken) => {

  return axios({
    method: "post",
    url: `${API}/${url}`,
    data: data,
    timeout: 600000,
    headers: headers(),
    cancelToken: cancelToken
  });
}

const apiFetch = (url, params = {}) => {
  return axios.get(`${API}/${url}${queryString(params)}`, {
    headers: headers(),
  });
};

const apiFetchWithSignal = (url, params = {}, cancelToken) => {
  // return axios.get(`${API}/${url}${queryString(params)}`, {
  //   headers: headers(),
  //   cancelToken: cancelToken 
  // })

  return axios({
    method: "get",
    url: `${API}/${url}`,
    data: params,
    timeout: 600000,
    headers: headers(),
    cancelToken: cancelToken
  });
};

// const getTimezones = () =>
//   apiFetch("Reference/TimezoneList").then((r) => ({
//     timezones: _.map(r.data, (x) => ({ value: x, label: x })),
//   }));

// const getCountryList = () =>
//   apiFetch("Reference/GetCountryList").then((r) => ({
//     countries : r.data,
//   }));

const api = {
  get_html_content(url) {
    try {
      if (url) {
        return axios.get(`${url}`, { headers: html_content_type_headers() });
      }
      return Promise.resolve(null);
    } catch {
      return Promise.resolve(null);
    }
   },

  fetch_raw(url, params = {}) {
    return axios.get(`${url}${queryString(params)}`, { headers: headers() });
  },

  fetch: apiFetch,

  fetchS: apiFetchWithSignal,

  upload(verb, url, data) {
    switch (verb.toLowerCase()) {
      case "post":
        return axios.post(`${API}/${url}`, data, { headers: headers() });
      case "put":
        return axios.put(`${API}/${url}`, data, { headers: headers() });
      case "patch":
        return axios.patch(`${API}/${url}`, data, { headers: headers() });
      default:
    }
  },

  post: post,

  postS: postWithSignal,

  put(url, data) {
    return axios.put(`${API}/${url}`, data, { headers: headers() });
  },

  patch(url, data) {
    return axios.patch(`${API}/${url}`, data, { headers: headers() });
  },

  delete(url) {
    return axios.delete(`${API}/${url}`, { headers: headers() });
  },

  post_form_data(url, formData) {
    return axios.post(`${API}/${url}`, formData, {
      headers: multipart_headers(),
    });
  },

  put_form_data(url, formData) {
    return axios.put(`${API}/${url}`, formData, {
      headers: multipart_headers(),
    });
  },

  getBlob(url, failureMessage) {
    return axios.get(`${API}/${url}`, {
      headers: headers(),
      withCredentials: true,
      responseType: "blob",
    }).then((response) => {
      const contentType = response.headers["content-type"];
      if (contentType === "application/json") {
        return {
          data: false,
          message: failureMessage ? failureMessage : "an error has occured please contact your admin..."
        };
      } else if (response.data?.size === 0) {
        return {
          data: false,
          message: failureMessage ? failureMessage : "The files contents have returned empty<br>Try again or please contact your admin..."
        };
      }
      return response;
    }).catch((error) => {
      console.error("API error:", error);
      return {
        data: false,
        message: failureMessage ? failureMessage : error.message
      };
    });
  },

  postGetBlob(url, data, failureMessage) {
    return axios.post(`${API}/${url}`, data, {
      headers: headers(),
      withCredentials: true,
      responseType: "blob",
    }).then(async (response) => {
      const contentType = response.headers["content-type"];
      if(contentType === "application/json") {
        // The server's own message wins (as in postDownloadBinary); the caller's text is the fallback.
        const serverMessage = await jsonBlobMessage(response.data);
        return {
          data: false,
          message: serverMessage || failureMessage || "an error has occured please contact your admin..."
        };
      } else {
        if (response.data?.size === 0) {
          return {
            data: false,
            message: failureMessage ? failureMessage : "The files contents have returned empty<br>Try again or please contact your admin..."
          };
        }
      }
      return response;
    }).catch(async (error) => {
      console.error("API error:", error);
      // A non-2xx (e.g. a 403 refusal) still carries its JSON message, as a Blob.
      const errorBody = error.response?.data;
      const isJson = (error.response?.headers?.["content-type"] || "").startsWith("application/json");
      const serverMessage = isJson && errorBody instanceof Blob ? await jsonBlobMessage(errorBody) : null;
      return {
        data: false,
        message: serverMessage || failureMessage || error.message
      };
    });
  },

  postFormDataGetBlob(url, formData) {
    return axios.post(`${API}${url}`, formData, {
      // headers: {
      //   // "Content-Type": "multipart/form-data",
      //   Authorization: `Bearer ${userToken()}`
      // },
      headers: multipart_headers(),
      withCredentials: true,
      responseType: "blob",
    });
  },

  getTimezones: getTimezones,
  getCountryList: getCountryList,
  getThreadSizes: getThreadSizes,
  getThreadColors: getThreadColors,
  getThreadMaterials: getThreadMaterials,
  getNeedleSizes: getNeedleSizes,
  userToken() {
    return userToken();
  },
  specToken() {
    return specToken();
  },
  isRateLimited,
  isHandledByInterceptor,
  toastFailure,
  catchHandler: (e) => {
    console.error(e);
  },
  postDownloadBinary(url, data, mimeType, exportName, failureMessage) {
    const headers = Object.assign(commonHeaders(), {
      'Content-Type': 'application/json',
      'Accept': mimeType
    });
    try {
      return axios.post(`${API}/${url}`, data, { responseType: 'arraybuffer', headers: headers }).then(response => {
        if (response.headers["content-type"] === "application/json") {
          const enc = new TextDecoder("utf-8");
          const stringResponse = enc.decode(response.data);
          const jsonResponse = JSON.parse(stringResponse);
          if (jsonResponse.message) {
            failureMessage = jsonResponse.message;
          }
          return {
            data: false,
            message: failureMessage ? failureMessage : "An error has occured please contact your admin..."
          };
        } 
        // first check if the file contents that returned are empty
        if (response.headers["content-length"] === "0") {
          return {
            data: false,
            message: failureMessage ? failureMessage : "The files contents have returned empty<br>Try again or please contact your admin..."
          };
        }
        var blob = new Blob([response.data], { type: `${mimeType};charset=utf-8;` });
        if (navigator.msSaveBlob) {
          navigator.msSaveBlob(blob, exportName);
        } else {
          var link = document.createElement("a");
          if (link.download !== undefined) {
            // Browsers that support HTML5 download attribute
            var url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", exportName);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        }        
      }).catch((err) => {
        // A non-2xx response (e.g. the report threw server-side) rejects here. Return the same
        // { data:false, message } sentinel the JSON-body and empty-content branches use so the
        // caller's existing `.then(res => res?.data === false && toast.error(res.message))` fires.
        // Previously this was swallowed to the console, so the user saw no error at all.
        console.error('Failed to postDownloadBinary:', err);
        return {
          data: false,
          message: failureMessage ? failureMessage : "An error has occured please contact your admin..."
        };
      });
    } catch (error) {
      console.error("API error:", error.message);
      return {
        data: false,
        message: failureMessage ? failureMessage : error.message
      };
    }
  }
};

export default api;
